import { getMatchIdsForUNL, getStatByName } from './misc';
import type { HistoricalData, PlayerResponse, TopScorerPlayer } from './types';

import { loadEntitiesByClassification } from '@apis/editorial';
import { EventsFilter, fetchPlayerEvents } from '@apis/events';
import { hydrateWithTranslations } from '@services/TranslatorService';
import { Order } from '@shared-types/common.types';
import { loadFromAPIs } from '@utilities/fetch';
import { uefaBaseUrl } from '@utilities/WindowContext';

export class DataHandler {
  callOptions: {
    apiUrl: string;
  };
  seasonYear: string;
  phase: string;
  competitionId: string;
  competitionName: string;
  competitionUrl: string;
  offset: number;
  limit: number;
  hardCodeUnl: boolean;
  usePhase: boolean;
  advSlotType: string;
  advPosition: string;
  advCampaign: string;
  language?: string;
  isFutsal: boolean;
  isWomen: boolean;
  enableEnhancedStatistics: boolean;

  init(params) {
    this.callOptions = {
      apiUrl: window.compStatsApiUrl,
    };
    this.seasonYear = params.season;
    this.phase = params.phase;
    this.competitionId = params.cup;
    this.competitionName = params.competitionName;
    this.competitionUrl = params.competitionUrl;
    this.offset = 0;
    this.limit = params.limit ?? 100;
    this.hardCodeUnl = params.hardCodeUnl;
    this.usePhase = params.usePhase;
    this.advSlotType = params.advSlotType;
    this.advPosition = params.advPosition;
    this.advCampaign = params.advCampaign;
    this.language = params.language;
    this.isFutsal = params.isFutsal;
    this.isWomen = params.isWomen;
    this.enableEnhancedStatistics = params.enableEnhancedStatistics;
  }

  async loadPlayersInfo() {
    const stats = 'goals';
    const params: {
      optionalFields: string;
      competitionId: string;
      seasonYear: string;
      stats: string;
      offset: number;
      limit: number;
      order: string;
      phase?: string;
    } = {
      optionalFields: 'PLAYER,TEAM',
      competitionId: this.competitionId,
      seasonYear: this.seasonYear,
      stats,
      offset: this.offset,
      limit: this.limit,
      order: Order.DESC,
    };
    if (this.usePhase && !this.hardCodeUnl) {
      params.phase = this.phase;
    }

    const response = await loadFromAPIs('/v1/player-ranking-leader', params, this.callOptions);
    let players: TopScorerPlayer[] = response?.[0]?.rankings?.map(
      (p: PlayerResponse): TopScorerPlayer => ({
        ...p,
        player: hydrateWithTranslations(p.player),
        team: hydrateWithTranslations(p.team),
      }),
    );
    if (!this.enableEnhancedStatistics) {
      players = players?.toSorted((a, b) => {
        const goalsA = getStatByName(a, 'goals');
        const goalsB = getStatByName(b, 'goals');

        if (goalsA === goalsB) {
          const nameA = a.player.lastName.toLowerCase();
          const nameB = b.player.lastName.toLowerCase();
          return nameA.localeCompare(nameB);
        }
        return goalsB - goalsA;
      });
    }
    return players?.filter((p) => getStatByName(p, 'goals') > 0);
  }

  loadJsonCarousel() {
    return loadFromAPIs<HistoricalData[]>(
      `/${this.competitionUrl}/top-scorer/libraries/json_history_carousel`,
      {},
      { apiUrl: uefaBaseUrl },
    );
  }

  loadEditorialInfo(playerId: string | string[]) {
    return loadEntitiesByClassification(
      {
        type: 'videostory,photostory',
        ['param.attributes.main.competition']: this.competitionId,
        ['param.attributes.main.season']: this.seasonYear,
        ['param.attributes.main.phase']: this.phase,
        ['param.attributes.main.section']: 'Editorial/TopScorer',
        ['param.attributes.footballEntities.player.playerId']: playerId,
      },
      this.language,
    );
  }

  async loadSinglePlayerInfo(playerId) {
    const stats =
      'attempts,attempts_on_target,attempts_off_target,attempts_on_woodwork,attempts_blocked,goals,assists,minutes_played_official,matches_appearance,goals_scored_with_left,goals_scored_with_right,goals_scored_head,goals_scored_other';
    const params: {
      competitionId: string;
      seasonYear: string;
      optionalFields: string;
      playerId: string;
      stats: string;
      matchId?: string[];
      phase?: string;
    } = {
      competitionId: this.competitionId,
      seasonYear: this.seasonYear,
      optionalFields: 'PLAYER,TEAM,AVERAGE',
      playerId,
      stats,
    };
    if (this.hardCodeUnl) {
      params.matchId = getMatchIdsForUNL();
    } else {
      params.phase = this.phase;
    }

    const response = await loadFromAPIs('v1/player-statistics/', params, this.callOptions);
    return response
      .map((p: PlayerResponse) => ({ ...p, player: hydrateWithTranslations(p.player) }))
      .map((p: PlayerResponse) => ({ ...p, team: hydrateWithTranslations(p.team) }));
  }

  loadMatchEvents(playerId: string) {
    const params = {
      competitionId: this.competitionId,
      seasonYear: this.seasonYear,
      filter: EventsFilter.GOALS,
      playerIds: playerId,
      competitionPhase: !this.hardCodeUnl ? this.phase : undefined,
      limit: 100,
      offset: 0,
    };

    return fetchPlayerEvents(params);
  }

  static apiInstance = new DataHandler();
  static getInstance(params?: Record<string, any>) {
    if (params) {
      this.apiInstance.init(params);
    }
    return this.apiInstance;
  }
}
